import { createElement, createContext, useContext } from 'react';
import { either } from 'fp-ts';
import { V2PromiseClient } from 'clarifai-web-grpc/proto/clarifai/api/service_grpc_web_pb';
import { cookieClientStorage } from 'utils/cookieStorage';
import getConfig from 'next/config';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { QueryKey, useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';
import {
  DeleteAnnotationsRequest,
  GetTaskCountRequest,
  ListAppKeysRequest,
  MultiCollaboratorsResponse,
  MultiInputsAddJobResponse,
  MultiKeyResponse,
  MultiTaskAssignmentResponse,
  PatchAnnotationsRequest,
  PatchInputsRequest,
  PostInputsUploadsRequest,
  PutTaskAssignmentsRequest,
  PutUploadContentPartsRequest,
  SingleTaskCountResponse,
  SingleUploadResponse,
} from 'api/grpc/service';
import {
  ConceptExtraInfoRequest,
  MultiModelTypeResponse,
  PatchConceptsRequest,
  PostTasksRequest,
} from 'clarifai-web-grpc/proto/clarifai/api/service_pb';
import { convertJsTaskToGrpcTask } from 'utils/grpc/jsToGrpc';
import { ClientReadableStream } from 'grpc-web';
import {
  Query,
  ConceptQuery,
  Input,
  UserAppIDSet,
  Struct,
  Error as GRPCError,
  Metadata,
  Search,
  Dataset,
  DatasetInput,
  BaseResponse,
  AnnotationFilter,
  Filter,
  Annotation,
  Status,
  StatusCode,
  BulkOperation,
  InputIDs,
  Operation,
  AddMetadata,
  DeleteMetadata,
  DeleteConcepts,
  AddConcepts,
  ConceptRelation,
  WorkflowModelUseCase,
  Workflow,
  WorkflowVersion,
  Model,
  ModelVersion,
} from './resources';
import {
  MultiAppResponse,
  ListAppsRequest,
  ListConceptsRequest,
  GetWorkflowRequest,
  ListConceptRelationsRequest,
  DeleteConceptRelationsRequest,
  SingleWorkflowResponse,
  GetConceptRequest,
  SingleConceptResponse,
  PostConceptsSearchesRequest,
  PostConceptRelationsRequest,
  MultiConceptResponse,
  MultiConceptRelationResponse,
  Pagination,
  PostAnnotationsRequest,
  MultiInputResponse,
  MultiAnnotationResponse,
  ListInputsRequest,
  PostInputsSearchesRequest,
  MultiSearchResponse,
  DeleteInputsRequest,
  DeleteAnnotationRequest,
  PostInputsRequest,
  PostDatasetInputsRequest,
  PostDatasetVersionsRequest,
  MultiDatasetInputResponse,
  MultiDatasetVersionResponse,
  DeleteDatasetInputsRequest,
  PostDatasetsRequest,
  MultiDatasetResponse,
  ListDatasetsRequest,
  PostAnnotationsSearchesRequest,
  ListModelsRequest,
  MultiModelResponse,
  PostBulkOperationsRequest,
  MultiBulkOperationsResponse,
  GetInputRequest,
  SingleInputResponse,
  SingleAppResponse,
  GetAppRequest,
  PatchAppRequest,
  ListAnnotationsRequest,
  PostModelOutputsRequest,
  PostWorkflowResultsRequest,
  PostWorkflowResultsResponse,
  MultiOutputResponse,
  ListCollaboratorsRequest,
  PostModelVersionsRequest,
  SingleModelResponse,
  ListTasksRequest,
  MultiTaskResponse,
  GetTaskRequest,
  SingleTaskResponse,
} from './service';
import { CLARIFAI_REQ_ID_PREFIX_HEADER_NAME } from '../../server/constants';

const { publicRuntimeConfig } = getConfig();

const GRPC_WEB_HOST = publicRuntimeConfig.GRPC_WEB_HOST || '';

// Timestamp of the last shown connection error to prevent duplicates
let lastConnectionErrorTimestamp = 0;
const CONNECTION_ERROR_DEBOUNCE_MS = 10000; // 10 seconds

/**
 * Provides access to the Clarifai API.
 * See [docs/docs/api/grpc.md](docs/docs/api/grpc.md) for more info.
 */
export class GRPCClient {
  _client: V2PromiseClient;

  constructor(hostname = GRPC_WEB_HOST) {
    const client = new V2PromiseClient(hostname, null, {
      'grpc.max_receive_message_length': -1,
      'grpc.max_send_message_length': -1,
      server_http_max_read_timeout: '1h',
      server_http_max_write_timeout: '1h',
    });
    this._client = client;
    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
      const devtoolsHook =
        window.__GRPCWEB_DEVTOOLS__ ||
        (() => {
          // eslint-disable-next-line no-console
          console.log('Visit https://github.com/SafetyCulture/grpc-web-devtools to install grpc web devtools for your browser');
        });
      devtoolsHook([client]);
    }
  }

  listConcepts(call: CF.API.ListConceptsCall): Promise<FP.Either<Error, MultiConceptResponse>> {
    const req = initRequest(ListConceptsRequest, call);

    return wrapReq(this._client.listConcepts(req, makeMetadata()));
  }

  addTask(call: Partial<CF.API.TaskCall>): Promise<FP.Either<Error, MultiTaskResponse>> {
    const req = initRequest(PostTasksRequest, call);
    const task = convertJsTaskToGrpcTask(call);
    req.setTasksList([task]);

    return wrapReq(this._client.postTasks(req, makeMetadata()));
  }

  deleteConceptRelations(call: CF.API.DeleteConceptRelationsCall): Promise<FP.Either<Error, BaseResponse>> {
    const req = initRequest(DeleteConceptRelationsRequest, call);
    req.setConceptId(call.conceptId);
    req.setIdsList(call.relationIds);

    return wrapReq(this._client.deleteConceptRelations(req, makeMetadata()));
  }

  postConceptRelations(call: CF.API.PostConceptRelationsCall): Promise<FP.Either<Error, MultiConceptRelationResponse>> {
    const req = initRequest(PostConceptRelationsRequest, call);
    if (call.conceptId) {
      req.setConceptId(call.conceptId);
    }

    const relation = new ConceptRelation();

    if (call.id) {
      relation.setId(call.id);
    }

    if (call.subjectConcept) {
      relation.setSubjectConcept(call.subjectConcept);
    }

    if (call.objectConcept) {
      relation.setObjectConcept(call.objectConcept);
    }

    req.setConceptRelationsList([relation]);

    if (call.predicate) {
      relation.setPredicate(call.predicate);
    }

    return wrapReq(this._client.postConceptRelations(req, makeMetadata()));
  }

  getWorkflow(call: CF.API.GetWorkflowCall): Promise<FP.Either<Error, SingleWorkflowResponse>> {
    const { workflowId } = call;
    const req = initRequest(GetWorkflowRequest, call);
    req.setWorkflowId(workflowId || '');
    return wrapReq(this._client.getWorkflow(req, makeMetadata()));
  }

  getConcept(call: CF.API.GetConceptCall, showErrorMessage = true): Promise<FP.Either<Error, SingleConceptResponse>> {
    const { conceptId } = call;
    const req = initRequest(GetConceptRequest, call);
    req.setConceptId(conceptId);

    if (showErrorMessage) {
      return wrapReq(this._client.getConcept(req, makeMetadata()));
    }

    return wrapReqWithoutErrorMessage(this._client.getConcept(req, makeMetadata()));
  }

  listConceptRelationsRequest(call: CF.API.ListConceptRelationsCall): Promise<FP.Either<Error, MultiConceptRelationResponse>> {
    const { conceptId } = call;
    const req = initRequest(ListConceptRelationsRequest, call);

    if (conceptId) {
      req.setConceptId(conceptId);
    }

    return wrapReq(this._client.listConceptRelations(req, makeMetadata()));
  }

  postConceptsSearches(call: CF.API.PostConceptsSearchesCall): Promise<FP.Either<Error, MultiConceptResponse>> {
    const {
      workflowId,
      name,
      includeMainAppConcepts,
      fuzzy = true,
      useCases,
      model: modelParam,
      workflow: workflowParam,
      outsideApp,
      rankableModel,
    } = call;
    const req = initRequest(PostConceptsSearchesRequest, call);

    const q = new ConceptQuery();
    if (fuzzy === false) {
      q.setName(name);
    } else {
      q.setName(`${name}*`);
    }

    if (!workflowParam && !modelParam) {
      const workflow = new Workflow();
      /**
       * This is a workaround. It's not obvious from front-end, but on backend,
       * if the workflow is set to Empty (which has no models),
       * the logic will search through the app's concepts only.
       */
      if (!includeMainAppConcepts) {
        workflow.setId('Empty');
      } else if (workflowId) {
        workflow.setId(workflowId);
      }

      q.setWorkflow(workflow);
    }

    if (useCases?.length) {
      const workflowModelUseCase: WorkflowModelUseCase[] = [];

      useCases.forEach((useCase) => {
        switch (useCase) {
          case 'CLASSIFICATION':
            workflowModelUseCase.push(WorkflowModelUseCase.CLASSIFICATION);
            break;
          case 'DETECTION':
            workflowModelUseCase.push(WorkflowModelUseCase.DETECTION);
            break;
          default:
            workflowModelUseCase.push(WorkflowModelUseCase.WORKFLOW_MODEL_USE_CASE_NOT_SET);
            break;
        }
      });

      q.setUseCasesList(workflowModelUseCase);
    }

    if (workflowParam) {
      const { id, version } = workflowParam;
      const workflow = new Workflow();

      workflow.setId(id);

      if (version?.id) {
        const workflowVersion = new WorkflowVersion();

        workflowVersion.setId(version.id);
        workflow.setVersion(workflowVersion);
      }

      q.setWorkflow(workflow);
    }

    if (modelParam) {
      const { id, model_version } = modelParam;
      const model = new Model();

      model.setId(id);

      if (model_version?.id) {
        const modelVersion = new ModelVersion();

        modelVersion.setId(model_version.id);
        model.setModelVersion(modelVersion);
      }

      q.setModel(model);
    }

    if (outsideApp) {
      q.setIgnoreAppConcepts(true);
    }

    if (rankableModel && rankableModel.getModelVersion()) {
      const model = new Model();
      model.setId(rankableModel.getId());
      model.setAppId(rankableModel.getAppId());
      model.setUserId(rankableModel.getUserId());
      const rankableModelVersionId = rankableModel.getModelVersion()?.getId();
      if (rankableModelVersionId) {
        const modelVersion = new ModelVersion();
        modelVersion.setId(rankableModelVersionId);
        model.setModelVersion(modelVersion);
        const extraInfo = new ConceptExtraInfoRequest();
        extraInfo.setRankableModel(model);
        req.setExtraInfo(extraInfo);
      }
    }

    req.setConceptQuery(q);

    return wrapReq(this._client.postConceptsSearches(req, makeMetadata()));
  }

  patchInputs(call: CF.API.PatchInputCall): Promise<FP.Either<Error, BaseResponse>> {
    const req = initRequest(PatchInputsRequest, call);
    const { inputList, action } = call;

    req.setInputsList(inputList);
    req.setAction(action);

    return wrapReq(this._client.patchInputs(req, makeMetadata()));
  }

  postBulkOperations(call: CF.API.PostBulkOperationCall): Promise<FP.Either<Error, MultiBulkOperationsResponse>> {
    const { inputIds, metadata, action, filters, concepts } = call;
    const req = initRequest(PostBulkOperationsRequest, call);
    const bulkOperation = new BulkOperation();

    if (inputIds) {
      const inputIdsList = new InputIDs();
      inputIdsList.setInputIdsList(inputIds);
      bulkOperation.setInputIds(inputIdsList);
    }

    if (filters) {
      const query = new Query();
      query.setFiltersList(filters);
      const search = new Search();
      search.setQuery(query);
      bulkOperation.setSearch(search);
    }

    if (action === 'add') {
      if (metadata) {
        const operation = new Operation();
        const addMetadata = new AddMetadata();
        addMetadata.setMetadata(Struct.fromJavaScript(metadata));
        operation.setAddMetadata(addMetadata);
        bulkOperation.setOperation(operation);
      } else if (concepts) {
        const operation = new Operation();
        const addConcepts = new AddConcepts();
        addConcepts.setConceptsList(concepts);
        operation.setAddConcepts(addConcepts);
        bulkOperation.setOperation(operation);
      }
    } else if (action === 'remove') {
      if (metadata) {
        const operation = new Operation();
        const deleteMetadata = new DeleteMetadata();
        deleteMetadata.setMetadata(Struct.fromJavaScript(metadata));
        operation.setAddMetadata(deleteMetadata);
        bulkOperation.setOperation(operation);
      } else if (concepts) {
        const operation = new Operation();
        const deleteConcepts = new DeleteConcepts();
        deleteConcepts.setConceptsList(concepts);
        operation.setDeleteConcepts(deleteConcepts);
        bulkOperation.setOperation(operation);
      }
    }

    req.setBulkOperationsList([bulkOperation]);

    return wrapReq(this._client.postBulkOperations(req, makeMetadata()));
  }

  postAnnotations(call: CF.API.PostAnnotationsCall): Promise<FP.Either<Error, MultiAnnotationResponse>> {
    const { annotations } = call;
    const req = initRequest(PostAnnotationsRequest, call);
    req.setAnnotationsList(annotations);

    return wrapReq(this._client.postAnnotations(req, makeMetadata()));
  }

  listAnnotations(call: CF.API.ListAnnotationsCall): Promise<FP.Either<Error, MultiAnnotationResponse>> {
    const req = initRequest(ListAnnotationsRequest, call);
    if (call.listAllAnnotations !== undefined) req.setListAllAnnotations(call.listAllAnnotations);
    if (call.inputIds !== undefined) req.setInputIdsList(call.inputIds);
    if (call.taskId !== undefined) req.setTaskId(call.taskId);

    if (call.userIds !== undefined) req.setUserIdsList(call.userIds);
    if (call.modelVersionIds !== undefined) req.setModelVersionIdsList(call.modelVersionIds);
    if (call.workflowVersionIds !== undefined) req.setWorkflowVersionIdsList(call.workflowVersionIds);
    if (call.statuses !== undefined) {
      req.setStatusesList(
        call.statuses.map((s) => {
          const status = new Status();
          status.setCode(s);
          return status;
        }),
      );
    }

    return wrapReq(this._client.listAnnotations(req, makeMetadata()));
  }

  listDatasets(call: CF.API.ListDatasetsCall): Promise<FP.Either<Error, MultiDatasetResponse>> {
    const req = initRequest(ListDatasetsRequest, call);

    if (call.search) {
      req.setSearch(call.search);
    }

    return wrapReq(this._client.listDatasets(req, makeMetadata()));
  }

  postAnnotationsSearches(call: CF.API.PostAnnotationsSearchesCall): Promise<FP.Either<Error, MultiSearchResponse>> {
    const req = initRequest(PostAnnotationsSearchesRequest, call);

    if (call.searches?.length) {
      req.setSearchesList(call.searches);
    } else {
      const status = new Status();
      status.setCode(StatusCode.ANNOTATION_SUCCESS);

      const anno = new Annotation();
      anno.setStatus(status);

      const filter = new Filter();
      filter.setAnnotation(anno);

      const query = new Query();
      query.setFiltersList([filter]);

      const search = new Search();
      search.setQuery(query);

      req.setSearchesList([search]);
    }

    return wrapReq(this._client.postAnnotationsSearches(req, makeMetadata()));
  }

  async postDatasets(call: CF.API.PostDatasetsCall): Promise<FP.Either<Error, MultiDatasetResponse>> {
    const req = initRequest(PostDatasetsRequest, call);

    // for each dataset we want to create, we'll first
    // create a default annotation filter
    // which simply selects annotations with a success status.
    const searches = call.datasetIds.map((id) => {
      const status = new Status();
      status.setCode(StatusCode.ANNOTATION_SUCCESS);

      const anno = new Annotation();
      anno.setStatus(status);

      const filter = new Filter();
      filter.setAnnotation(anno);

      const query = new Query();
      query.setFiltersList([filter]);

      const search = new Search();
      search.setId(`${id}-default`);
      search.setQuery(query);
      search.setSave(true);

      return search;
    });

    await this.postAnnotationsSearches({
      ...call,
      searches,
      page: 1,
      perPage: 128,
    });

    // now that the searches have been created, let's create our datasets
    const datasets = call.datasetIds.map((id) => {
      const search = new Search();
      search.setId(`${id}-default`);

      const annoFilter = new AnnotationFilter();
      annoFilter.setSearch(search);
      const dataset = new Dataset();
      dataset.setId(id);
      dataset.setDefaultAnnotationFilter(annoFilter);
      return dataset;
    });

    req.setDatasetsList(datasets);
    return wrapReq(this._client.postDatasets(req, makeMetadata()));
  }

  postDatasetVersions(call: CF.API.PostDatasetVersionsCall): Promise<FP.Either<Error, MultiDatasetVersionResponse>> {
    const req = initRequest(PostDatasetVersionsRequest, call);
    const userAppIdSet = new UserAppIDSet();
    userAppIdSet.setUserId(call.userOrOrgId);
    userAppIdSet.setAppId(call.appId);
    req.setUserAppId(userAppIdSet);
    req.setDatasetId(call.datasetId);
    req.setDatasetVersionsList(call.datasetVersions);
    return wrapReq(this._client.postDatasetVersions(req, makeMetadata()));
  }

  postDatasetInputs(call: CF.API.PostDatasetInputsCall): Promise<FP.Either<Error, MultiDatasetInputResponse>> {
    const req = initRequest(PostDatasetInputsRequest, call);
    if (call.inputIds) {
      const datasetInputs = call.inputIds.map((id) => {
        const input = new Input();
        input.setId(id);

        const dsInput = new DatasetInput();
        dsInput.setInput(input);
        return dsInput;
      });
      req.setDatasetInputsList(datasetInputs);
    }

    if (call.filters) {
      const query = new Query();
      query.setFiltersList(call.filters);
      const search = new Search();
      search.setQuery(query);
      req.setSearch(search);
    }

    req.setDatasetId(call.datasetId);

    return wrapReq(this._client.postDatasetInputs(req, makeMetadata()));
  }

  deleteDatasetInputs(call: CF.API.DeleteDatasetInputsCall): Promise<FP.Either<Error, BaseResponse>> {
    const req = initRequest(DeleteDatasetInputsRequest, call);
    req.setDatasetId(call.datasetId);
    if (call.inputIds) {
      req.setInputIdsList(call.inputIds);
    }

    return wrapReq(this._client.deleteDatasetInputs(req, makeMetadata()));
  }

  listInputs(call: CF.API.ListInputsCall): Promise<FP.Either<Error, MultiInputResponse>> {
    const req = initRequest(ListInputsRequest, call);
    if (call.inputIds) {
      req.setIdsList(call.inputIds);
    }
    return wrapReq(this._client.listInputs(req, makeMetadata()));
  }

  getInput(call: CF.API.GetInputCall): Promise<FP.Either<Error, SingleInputResponse>> {
    const req = initRequest(GetInputRequest, call);
    const inputId = req.setInputId(call.inputId);
    return wrapReq(this._client.getInput(inputId, makeMetadata()));
  }

  postInputsSearches(call: CF.API.PostInputsSearchesCall): Promise<FP.Either<Error, MultiSearchResponse>> {
    const req = initRequest(PostInputsSearchesRequest, call);

    const query = new Query();

    query.setFiltersList(call.filters);
    query.setRanksList(call.ranks);

    const search = new Search();
    search.setQuery(query);

    req.setSearchesList([search]);
    return wrapReq(this._client.postInputsSearches(req, makeMetadata()));
  }

  patchConcepts(call: CF.API.PatchConceptsCall): Promise<FP.Either<Error, MultiConceptResponse>> {
    const { concepts, action } = call;
    const req = initRequest(PatchConceptsRequest, call);
    req.setConceptsList(concepts);
    req.setAction(action);

    return wrapReq(this._client.patchConcepts(req, makeMetadata()));
  }

  patchAnnotations(call: CF.API.PatchAnnotationsCall): Promise<FP.Either<Error, MultiAnnotationResponse>> {
    const req = initRequest(PatchAnnotationsRequest, call);

    req.setAnnotationsList(call.annotations);
    req.setAction(call.action);
    if (call.deleteIfEmptyData) {
      req.setDeleteIfEmptyData(call.deleteIfEmptyData);
    }
    return wrapReq(this._client.patchAnnotations(req, makeMetadata()));
  }

  deleteAnnotation(call: CF.API.DeleteAnnotationCall): Promise<FP.Either<Error, BaseResponse>> {
    const req = initRequest(DeleteAnnotationRequest, call);

    req.setInputId(call.inputId);
    req.setAnnotationId(call.annotationId);

    return wrapReq(this._client.deleteAnnotation(req, makeMetadata()));
  }

  deleteAnnotations(call: CF.API.DeleteAnnotationsCall): Promise<FP.Either<Error, BaseResponse>> {
    const req = initRequest(DeleteAnnotationsRequest, call);

    req.setIdsList(call.annotationIds);
    req.setInputIdsList(call.inputIds);

    return wrapReq(this._client.deleteAnnotations(req, makeMetadata()));
  }

  deleteInputs(call: CF.API.DeleteInputsCall): Promise<FP.Either<Error, BaseResponse>> {
    const req = initRequest(DeleteInputsRequest, call);

    req.setIdsList(call.inputIds);
    return wrapReq(this._client.deleteInputs(req, makeMetadata()));
  }

  patchApp(call: CF.API.PatchAppCall): Promise<FP.Either<Error, SingleAppResponse>> {
    const req = initRequest(PatchAppRequest, call);
    req.setAction(call.action);
    req.setApp(call.app);
    return wrapReq(this._client.patchApp(req, makeMetadata()));
  }

  getApp(call: CF.API.GetAppCall): Promise<FP.Either<Error, SingleAppResponse>> {
    const req = initRequest(GetAppRequest, call);
    return wrapReq(this._client.getApp(req, makeMetadata()));
  }

  listApps(call: CF.API.ListAppsCall): Promise<FP.Either<Error, MultiAppResponse>> {
    const req = new ListAppsRequest();
    const userAppIdSet = new UserAppIDSet();
    userAppIdSet.setUserId(call.userId);
    req.setUserAppId(userAppIdSet);
    return wrapReq(this._client.listApps(req, makeMetadata()));
  }

  postInputs(call: CF.API.PostInputsCall): Promise<FP.Either<Error, MultiInputResponse>> {
    const req = initRequest(PostInputsRequest, call);
    req.setInputsList(call.inputs);
    if (call.jobId) {
      req.setInputsAddJobId(call.jobId);
    }
    return wrapReq(this._client.postInputs(req, makeMetadata()));
  }

  postInputsUpload(call: CF.API.PostInputsUploadsCall): Promise<FP.Either<Error, MultiInputsAddJobResponse>> {
    const req = initRequest(PostInputsUploadsRequest, call);
    req.addInputsUploads(call.inputsUpload);
    // BE have an error that it says plural but it's singular (addInputsUploads, but it's only one input)
    return wrapReq(this._client.postInputsUploads(req, makeMetadata()));
  }

  putUploadContentPart(call: CF.API.PutUploadContentPartRequestCall): Promise<FP.Either<Error, SingleUploadResponse>> {
    const req = initRequest(PutUploadContentPartsRequest, call);
    req.setUploadId(call.uploadId);
    req.addContentParts(call.contentPart);
    return wrapReq(this._client.putUploadContentParts(req, makeMetadata()));
  }

  listAppKeys(call: CF.API.UserAppCall): Promise<FP.Either<Error, MultiKeyResponse>> {
    const req = initRequest(ListAppKeysRequest, call);
    return wrapReq(this._client.listAppKeys(req, makeMetadata()));
  }

  listModels(call: CF.API.ListModelsCall): Promise<FP.Either<Error, MultiModelResponse>> {
    const { modelTypeId, name } = call;
    const req = initRequest(ListModelsRequest, call);

    if (modelTypeId) {
      req.setModelTypeId(modelTypeId);
    }

    if (name) {
      req.setName(`*${name}*`);
    }

    return wrapReq(this._client.listModels(req, makeMetadata()));
  }

  listModelTypes(call: CF.API.ListModelTypesCall): Promise<FP.Either<Error, MultiModelTypeResponse>> {
    const req = initRequest(ListModelsRequest, call);
    return wrapReq(this._client.listModelTypes(req, makeMetadata()));
  }

  postModelOutputs(call: CF.API.PostModelOutputsCall): Promise<MultiOutputResponse> {
    const { modelId, modelVersionId, inputs, model, runnerSelector } = call;
    const req = initRequest(PostModelOutputsRequest, call);

    if (modelVersionId) {
      req.setVersionId(modelVersionId);
    }

    req.setModelId(modelId);
    req.setInputsList(inputs);

    if (model) {
      req.setModel(model);
    }

    if (runnerSelector) {
      req.setRunnerSelector(runnerSelector);
    }

    return wrapReqPromise(this._client.postModelOutputs(req, makeMetadata()));
  }

  generateModelOutputsDirect(req: PostModelOutputsRequest): ClientReadableStream<MultiOutputResponse> {
    return this._client.generateModelOutputs(req, makeMetadata());
  }

  postModelOutputsDirect(req: PostModelOutputsRequest): Promise<MultiOutputResponse> {
    return this._client.postModelOutputs(req, makeMetadata());
  }

  generateModelOutputs(call: CF.API.PostModelOutputsCall): ClientReadableStream<MultiOutputResponse> {
    const { modelId, modelVersionId, inputs, model, runnerSelector } = call;
    const req = initRequest(PostModelOutputsRequest, call);

    if (modelVersionId) {
      req.setVersionId(modelVersionId);
    }

    if (runnerSelector) {
      req.setRunnerSelector(runnerSelector);
    }

    req.setModelId(modelId);
    req.setInputsList(inputs);

    if (model) {
      req.setModel(model);
    }

    return this._client.generateModelOutputs(req, makeMetadata());
  }

  postWorkflowResults(call: CF.API.PostWorkflowResultsCall): Promise<PostWorkflowResultsResponse> {
    const { workflowId, workflowVersionId, inputs } = call;
    const req = initRequest(PostWorkflowResultsRequest, call);

    req.setWorkflowId(workflowId);
    req.setVersionId(workflowVersionId);
    req.setInputsList(inputs);

    return wrapReqPromise(this._client.postWorkflowResults(req, makeMetadata()));
  }

  listCollaborators(call: CF.API.ListCollaboratorsCall): Promise<FP.Either<Error, MultiCollaboratorsResponse>> {
    const { listAllCollaborators = false } = call;
    const req = initRequest(ListCollaboratorsRequest, call);

    req.setListAllCollaborators(listAllCollaborators);

    return wrapReq(this._client.listCollaborators(req, makeMetadata()));
  }

  putTaskAssignments(call: CF.API.PutTaskAssignmentsCall): Promise<FP.Either<Error, MultiTaskAssignmentResponse>> {
    const { taskId, action, labelSubmitConfig, reviewStartConfig, reviewApproveConfig, reviewRequestChangesConfig, reviewRejectConfig } = call;
    const req = initRequest(PutTaskAssignmentsRequest, call);

    req.setTaskId(taskId);
    req.setAction(action);

    // Note: these conditions are required, because this request support oneof ONLY (from the list below)
    if (labelSubmitConfig) {
      req.setLabelSubmitConfig(labelSubmitConfig);
    } else if (reviewStartConfig) {
      req.setReviewStartConfig(reviewStartConfig);
    } else if (reviewApproveConfig) {
      req.setReviewApproveConfig(reviewApproveConfig);
    } else if (reviewRequestChangesConfig) {
      req.setReviewRequestChangesConfig(reviewRequestChangesConfig);
    } else if (reviewRejectConfig) {
      req.setReviewRejectConfig(reviewRejectConfig);
    }

    return wrapReq(this._client.putTaskAssignments(req, makeMetadata()));
  }

  postModelVersion(call: CF.API.PostModelVersionCall): Promise<SingleModelResponse> {
    const { modelId, modelVersion } = call;
    const req = initRequest(PostModelVersionsRequest, call);
    req.setModelId(modelId);
    req.setModelVersionsList([modelVersion]);
    return wrapReqPromise(this._client.postModelVersions(req, makeMetadata()));
  }

  listTasks(call: CF.API.LabelerTask.ListTasksGrpcRequest): Promise<FP.Either<Error, MultiTaskResponse>> {
    const req = initRequest(ListTasksRequest, call);

    req.setAdditionalFieldsList(call.params || []);

    return wrapReq(this._client.listTasks(req, makeMetadata()));
  }

  getTask(call: CF.API.LabelerTask.GetTaskGrpcRequest): Promise<FP.Either<Error, SingleTaskResponse>> {
    const req = initRequest(GetTaskRequest, call);

    req.setTaskId(call.taskId);
    req.setAdditionalFieldsList(call.additionalFieldsList || []);

    return wrapReq(this._client.getTask(req, makeMetadata()));
  }

  getTaskInputCount(call: CF.API.LabelerTask.GetTaskCountRequest): Promise<SingleTaskCountResponse> {
    const req = initRequest(GetTaskCountRequest, call);

    req.setTaskId(call.taskId);

    if (call.userIdList?.length) {
      req.setUserIdsList(call.userIdList);
    }

    if (call.modelVersionIdList?.length) {
      req.setModelVersionIdsList(call.modelVersionIdList);
    }

    if (call.workflowVersionIdList?.length) {
      req.setWorkflowVersionIdsList(call.workflowVersionIdList);
    }

    return wrapReqPromise(this._client.getTaskInputCount(req, makeMetadata()));
  }
}

function initRequest<T extends BaseRequest>(
  RequestClass: { new (): T },
  call: Partial<CF.API.UserAppCall & CF.API.PaginatedCall & { setOnlyCount?: boolean }>,
): T {
  const req = new RequestClass();
  if (typeof req.setUserAppId === 'function' && call.userOrOrgId !== undefined && call.appId !== undefined) {
    const userAppIdSet = new UserAppIDSet();
    userAppIdSet.setUserId(call.userOrOrgId);
    userAppIdSet.setAppId(call.appId);
    req.setUserAppId(userAppIdSet);
  }

  if (typeof req.setPage === 'function' && typeof req.setPerPage === 'function') {
    if (call.page !== undefined) {
      req.setPage(call.page);
    }
    if (call.perPage !== undefined) {
      req.setPerPage(call.perPage);
    }
  }

  if (typeof req.setOnlyCount === 'function' && call.setOnlyCount !== undefined) {
    req.setOnlyCount(call.setOnlyCount);
  } else if (typeof req.setPagination === 'function') {
    const pagination = new Pagination();
    if (call.page !== undefined) {
      pagination.setPage(call.page);
    }
    if (call.perPage !== undefined) {
      pagination.setPerPage(call.perPage);
    }
    req.setPagination(pagination);
  }
  return req;
}

export const grpcClient = new GRPCClient(GRPC_WEB_HOST);

const GRPCClientContext = createContext<GRPCClient | undefined>(undefined);

export function useGRPCClient(): GRPCClient {
  const client = useContext(GRPCClientContext);

  if (!client) {
    throw new Error('NoContext: GRPCClient. Please wrap your Next.js Page with <GRPCClientProvider> to give it access to a GRPCClient');
  }

  return client;
}

export interface GRPCClientProviderProps {
  client: GRPCClient;
}

export function GRPCClientProvider(props: React.PropsWithChildren<GRPCClientProviderProps>): JSX.Element {
  return createElement(GRPCClientContext.Provider, { value: props.client }, props.children);
}

/**
 * A GRPC callback. Uses the traditional node.js style
 * callback pattern of (err, r esp).
 */
export type GRPCCallback<Res> = (grpcErr: GRPCError, res: Res) => void;

interface BaseRequest {
  setUserAppId?(value?: UserAppIDSet): void;
  setPage?(value: number): void;
  setPerPage?(value: number): void;
  setPagination?(value: Pagination): void;
  setOnlyCount?(value: boolean): void;
}

async function wrapReqPromise<Res extends BaseResponse>(inner: Promise<Res>): Promise<Res> {
  try {
    const res = await inner;
    const status = res.getStatus();

    // allow mixed status, caller can decide how to handle it
    if (status && status.getCode() !== StatusCode.SUCCESS && status.getCode() !== StatusCode.MIXED_STATUS) {
      const statusObj = status.toObject();
      const err = new Error(JSON.stringify(status.toObject()));
      console.error(err);
      cogoToast.error(
        <div>
          <div>{statusObj.code}</div>
          <div>{statusObj.details}</div>
        </div>,
        { heading: statusObj.description, data: statusObj, enableThrottle: true },
      );
    }
    return res;
  } catch (errorResponse) {
    const grpcErr = errorResponse as GRPCError;
    console.error(grpcErr);

    return Promise.reject(grpcErr);
  }
}

async function wrapReq<Res extends BaseResponse>(inner: Promise<Res>): Promise<FP.Either<Error, Res>> {
  try {
    const res = await inner;
    const status = res.getStatus();

    // allow mixed status, caller can decide how to handle it
    if (status && status.getCode() !== StatusCode.SUCCESS && status.getCode() !== StatusCode.MIXED_STATUS) {
      const statusObj = status.toObject();
      const err = new Error(JSON.stringify(status.toObject()));
      console.error(err);
      // Use the refactored toastMessage function - simplified message

      cogoToast.error(
        <div>
          <div>{statusObj.code}</div>
          <div>{statusObj.details}</div>
        </div>,
        { heading: statusObj.description, data: statusObj, enableThrottle: true },
      );

      return either.left(err);
    }

    return either.right(res);
  } catch (e: unknown) {
    const grpcErr = e as GRPCError;
    console.error(grpcErr);

    // Check for connection-related errors (UNKNOWN(2), UNAVAILABLE(40031), EXTERNAL_CONNECTION_ERROR(40034))
    if (grpcErr.code === 2 || grpcErr.code === 40031 || grpcErr.code === 40034) {
      const now = Date.now();
      // Debounce connection errors
      if (now - lastConnectionErrorTimestamp > CONNECTION_ERROR_DEBOUNCE_MS) {
        // Use the refactored toastMessage function
        cogoToast.error('Connection Error: Unable to reach Clarifai servers. Please check your internet connection and try again.');
        lastConnectionErrorTimestamp = now;
      }
      // Return a generic connection error
      return either.left(new Error(`[${grpcErr.code}] Connection Error: ${grpcErr.message}`));
    } else {
      // Handle other gRPC errors - Use the refactored toastMessage function
      cogoToast.error(grpcErr.message);
      return either.left(new Error(`[${grpcErr.code}] ${grpcErr.message}`));
    }
  }
}

async function wrapReqWithoutErrorMessage<Res extends BaseResponse>(inner: Promise<Res>): Promise<FP.Either<Error, Res>> {
  try {
    const res = await inner;
    const status = res.getStatus();

    // allow mixed status, caller can decide how to handle it
    if (status && status.getCode() !== StatusCode.SUCCESS && status.getCode() !== StatusCode.MIXED_STATUS) {
      const err = new Error(JSON.stringify(status.toObject()));

      console.error(err);

      return either.left(err);
    }

    return either.right(res);
  } catch (e: unknown) {
    const grpcErr = e as GRPCError;

    console.error(grpcErr);

    return either.left(new Error(`[${grpcErr.code}] ${grpcErr.message}`));
  }
}

function makeMetadata(): Metadata {
  const md: Metadata = {};
  const token = cookieClientStorage.get('session_token');

  md[CLARIFAI_REQ_ID_PREFIX_HEADER_NAME] = 'webportal-grpc';

  if (token) {
    md['X-Clarifai-Session-Token'] = token;
  }

  return md;
}

/**
 * A thin wrapper around `useQuery` to make working with
 * grpc client easier in react components.
 * @param key The query's cache key
 * @param executor A function that receives the client object, performs a
 * query request, and returns a Promise of a result.
 * @returns A UseQuery result for the grpc request.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useGRPCQuery<T extends BaseResponse>(
  key: QueryKey,
  executor: (client: GRPCClient) => Promise<FP.Either<Error, T>>,
  options?: UseQueryOptions<FP.Either<Error, T>, Error>,
) {
  const grpc = useGRPCClient();
  return useQuery(key, () => executor(grpc), options);
}

/**
 * A thin wrapper around `useMutation` to make working with grpc
 * client easier in react components.
 * @param key The query's mutation key
 * @param executor A function that receives the client object, performs
 * a mutation request, and returns a Promise of a result.
 * @returns A UseMutation result for the grpc request.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useGRPCMutation<T extends BaseResponse>(
  executor: (client: GRPCClient) => Promise<FP.Either<Error, T>>,
  options?: UseMutationOptions<FP.Either<Error, T>, Error>,
) {
  const grpc = useGRPCClient();
  return useMutation(() => executor(grpc), options);
}
